import  { useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import css from './TnaModuleDetails.module.css';

import TnaModulesGeneral from './TnaModulesGeneral';
import TnaModuleEdit from './TnaModuleEdit';
import TnaModuleGeneralEdit from './TnaModuleGeneralEdit';
import TnaRow from './TnaRow';

import useTnaModule from '../../../../../hooks/useTnaModule';
import useTnaDiff from '../../../../../hooks/useTnaDiff';

import * as tnaServices from '../../../../../services/tnaServices';
import { machineOptions } from '../configuration';
import { hhmmToHhmmss, joinCsv, sliceHhmm, splitCsv } from '../../../../../services/utils/tna';


export default function TnaModuleDetails() {
  const { tnaModuleId } = useParams();

  const {
    moduleDetails,
    setModuleDetails,
    allAtas,
    isForDiff,
    loading,
    error,
    ataById,
    totalDuration,
  } = useTnaModule(tnaModuleId);

  const { consolidatedById } = useTnaDiff(moduleDetails, allAtas);

  const [machine, setMachine] = useState(machineOptions);
  const [editingAta, setEditingAta] = useState(null);
  const [modal, setModal] = useState(false);
  const [editForm, setEditForm] = useState({
    tnaModuleAtaId: '',
    components: '',
    objectives: '',
    level: '',
    methods: [],  // normalized
    aids: [],     // normalized
    duration: '00:00', // normalized HH:MM
  });

  const filteredAtas = useMemo(
    () => allAtas.filter((ata) => machine[ata.ataPart]),
    [machine, allAtas]
  );

  const toggleModal = useCallback(() => setModal((m) => !m), []);

  const filterOnChange = useCallback((e) => {
    const { name, checked } = e.target;
    setMachine(prev => ({ ...prev, [name]: checked }));
  }, []);

  const getAtaAdditionalData = useCallback(
    (ataId) => ataById.get(ataId) || null,
    [ataById]
  );

  const handleEditClick = useCallback((ata) => {
    const a = getAtaAdditionalData(ata.ataId);
    setEditingAta(ata);
    setEditForm({
      tnaModuleAtaId: a?.tnaModuleAtaId || '',
      objectives: a?.objectives || '',
      components: a?.components || '',
      level: a?.level || '',
      methods: splitCsv(a?.methods || ''),
      aids: splitCsv(a?.aids || ''),
      duration: sliceHhmm(a?.duration), // HH:MM
    });
  }, [getAtaAdditionalData]);

  const handleCancelEdit = useCallback(() => {
    setEditingAta(null);
    setEditForm({
      tnaModuleAtaId: '',
      objectives: '',
      components: '',
      level: '',
      methods: [],
      aids: [],
      duration: '00:00',
    });
  }, []);

  const handleSaveEdit = useCallback(async () => {
    console.log(editingAta);
    
    if (!editingAta) return;
    const payload = {
      tnaModuleAtaId: editForm.tnaModuleAtaId,
      tnaModuleId: moduleDetails.tnaModuleId,
      ataId: editingAta.ataId,
      ataNo: String(editingAta.ataNo),
      ataTitle: editingAta.ataTitle,
      objectives: editForm.objectives,
      components: editForm.components,
      level: editForm.level,
      methods: joinCsv(editForm.methods),
      aids: joinCsv(editForm.aids),
      duration: hhmmToHhmmss(editForm.duration),
    };
    console.log(payload);
    
    try {
      await tnaServices.updateModuleAta(payload);
      const res = await tnaServices.getModuleById(moduleDetails.tnaModuleId);
      setModuleDetails(res.data?.[0] || {});
      handleCancelEdit();
    } catch (e) {
      console.error('Error updating module ATA:', e);
    }
  }, [editingAta, editForm, moduleDetails?.tnaModuleId, setModuleDetails, handleCancelEdit]);

  const onAdd = useCallback((e) => {
    e.preventDefault();
    // handleSubmit(e);
    // toggleModal();
  }, []);

  if (loading) return <p>Loading module...</p>;
  if (error) return <p className={css.error}>Failed to load module.</p>;

  return (
    <>
      <h2>{moduleDetails.tnaModuleTitle}</h2>

      <TnaModulesGeneral
        moduleDetails={moduleDetails}
        calculateTotalDuration={() => totalDuration}
        toggleModal={toggleModal}
      />

      <div className={css.course}>
        <h2 className={css.h2}>Details</h2>

        <div className={css.filters}>
          {Object.keys(machine).map((key) => (
            <div key={key}>
              <input
                type="checkbox"
                name={key}
                id={key}
                checked={!!machine[key]}
                onChange={filterOnChange}
              />
              <label htmlFor={key} className={css.label}>{key}</label>
            </div>
          ))}
        </div>

        <table className={css.table}>
          <thead className={css.thead}>
            <tr>
              <th className={`${css.th} ${css.center}`}>ATA</th>
              <th className={css.th}>Title</th>
              {isForDiff && <th className={`${css.th} ${css.left}`}>Comp diff</th>}
              <th className={`${css.th} ${css.left}`}>Components</th>
              {isForDiff && <th className={`${css.th} ${css.left}`}>Obj diff</th>}
              <th className={`${css.th} ${css.left}`}>Objectives</th>
              {isForDiff && <th className={`${css.th} ${css.left}`}>Lvl diff</th>}
              <th className={`${css.th} ${css.center}`}>Level</th>
              <th className={`${css.th} ${css.center}`}>Aids</th>
              <th className={`${css.th} ${css.center}`}>Methods</th>
              <th className={`${css.th} ${css.center}`}>Duration</th>
              <th className={`${css.th} ${css.center}`}>Actions</th>
            </tr>
          </thead>

          <tbody className={css.tbody}>
            {filteredAtas.length ? (
              filteredAtas.map((ata) => {
                const additionalData = getAtaAdditionalData(ata.ataId);
                const diffRow = consolidatedById.get(ata.ataId);
                return (
                  <TnaRow
                    key={ata.ataId}
                    ata={ata}
                    additionalData={additionalData}
                    showDiff={!!isForDiff}
                    diffRow={diffRow}
                    onEditClick={handleEditClick}
                  />
                );
              })
            ) : (
              <tr>
                <td colSpan={11} style={{ textAlign: 'center' }}>
                  No matching records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {editingAta && (
          <TnaModuleEdit
            editingAta={editingAta}
            editForm={editForm}
            setEditForm={setEditForm}
            handleCancelEdit={handleCancelEdit}
            handleSaveEdit={handleSaveEdit}
            showDiff={!!isForDiff}
            diffRow={consolidatedById.get(editingAta.ataId)}
          />
        )}

        {modal && (
          <TnaModuleGeneralEdit
            tnaModuleData={moduleDetails}
            toggleModal={toggleModal}
            method="edit"
            onAdd={onAdd}
          />
        )}
      </div>
    </>
  );
}
